import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { usePage } from '@inertiajs/react';
import Cookies from 'js-cookie';
import {
    getInitData,
    updateMeta,
    updateStatus,
    updateRequest,
    submitRequest,
    updateOptions,
} from '@/actions/wizard/RequestWizardActions';
import { useCountries } from '@/hooks/useCountries';
import { useStep } from './useStep';
import { parseMcpParams, getCompletedSteps } from './useMcpPrefill';

const useWizard = () => {
    const dispatch = useDispatch();
    const { status, meta, options } = useSelector(
        (state) => state.requestWizard
    );
    const { activeStep, availableSteps, prev, toStep } = useStep();
    const countries = useCountries();
    const pendingRequest = is_node ? false : sessionStorage.getItem('yh-pending-request');

    /**
     * MARK: Initialization
     */

    const init = ({
        modalId = false,
        embed = false,
        autoShow = false,
        isMobile = false,
    }) => {
        // Prevent re-initialization
        if (status.init) return;

        // Dispatch getInitData BEFORE marking init=true — the action's own
        // status.init guard would otherwise short-circuit the API call.
        const initPromise = dispatch(getInitData());
        dispatch(updateStatus('init', true));

        // If there's a pending request (after social auth), submit it
        if (pendingRequest) {
            submitPendingRequest();
            return;
        }

        // Check for MCP prefill parameters
        const mcpPrefill = parseMcpParams();
        if (mcpPrefill) {
            // Wait for getInitData to complete before applying MCP prefill
            // so the API response doesn't overwrite our prefilled steps.
            if (initPromise && initPromise.then) {
                initPromise
                    .then(() => applyMcpPrefill(mcpPrefill, embed, modalId))
                    .catch(() => applyMcpPrefill(mcpPrefill, embed, modalId));
            } else {
                applyMcpPrefill(mcpPrefill, embed, modalId);
            }
            return;
        }

        // Set locale to customer's IP location
        let country = 'GB';
        let currencySign = '£';
        try {
            country = usePage().props.init.locale.country;
            currencySign = usePage().props.init.locale.currencySign;
        } catch (error) {
            // usePage hook isn't available (page isn't served by Inertia)
            if (global?.locale) {
                country = global.locale.country ?? 'GB';
                currencySign = global.locale.currencySign ?? '£';
            }
        }

        // Validate detected country is one we support, default to GB
        if (!countries[country]) {
            country = 'GB';
            currencySign = '£';
        }

        let newMeta = {
            embed,
            modalId,
            modal: !embed,
            isMobile,
            currencySign,
        };

        if (embed && !route()?.current('quote.embed')) {
            newMeta = {
                ...newMeta,
                startedAt: new Date(),
                source: 'wordpress',
            };
        }

        if (route()?.current('quote.embed')) {
            newMeta = {
                ...newMeta,
                source: 'blog',
            };
        }

        dispatch(updateMeta(newMeta));
        dispatch(updateRequest({ country }));

        // When in modal
        if (!embed) {
            // Auto show modal
            modalAutoShow(modalId, autoShow);
        }
    };

    /**
     * MARK: Methods
     */
    const showModal = (modalId = meta.modalId) =>
        $('#' + modalId).modal('show');

    const hideModal = (modalId = meta.modalId) =>
        $('#' + modalId).modal('hide');

    const modalAutoShow = (modalId, autoShow = false) => {
        const cookieStatus = Cookies.get('yh-bespoke-quote');

        // Don't show if cookie is set or autoShow is disabled
        if (!autoShow || cookieStatus) return;

        setTimeout(() => {
            // Don't show when login modal is visible
            if ($('#customerLoginModal').hasClass('show')) return;

            showModal(modalId);

            // Update meta
            dispatch(
                updateMeta({
                    autoShow: true,
                    autoShowDelay: autoShow,
                    source: 'popup',
                    ctaCopy: false,
                })
            );

            // Set cookie to prevent showing again
            Cookies.set('yh-bespoke-quote', true);
        }, autoShow);
    };

    const exit = (source, copy) => {
        dispatch(updateMeta({ exitSource: source, exitCopy: copy }));
        // tracking will be handled by event listener
        hideModal();
    };

    const delaySubmit = useCallback(
        debounce(() => dispatch(submitRequest()), 300),
        []
    );

    const submit = () => {
        if (!canSubmit()) return;

        dispatch(updateStatus('submitting', true));
        delaySubmit();
    };

    const submitPendingRequest = () => {
        const {
            meta: pendingMeta,
            request: pendingRequestData,
            steps: pendingSteps,
        } = JSON.parse(pendingRequest);

        dispatch(updateStatus('pending', true));
        dispatch(updateMeta(pendingMeta));
        dispatch(updateRequest(pendingRequestData));
        dispatch(
            // Retain steps progress
            updateOptions(
                'steps',
                options.steps.map((step) => {
                    const pendingStep = pendingSteps.find(
                        (ps) => ps.name === step.name
                    );
                    return {
                        ...step,
                        isComplete: pendingStep.isComplete,
                        error: pendingStep.error,
                    };
                })
            )
        );

        // Wait for rendering
        setTimeout(() => {
            if (!pendingMeta.embed) {
                showModal(pendingMeta.modalId);
            }
            dispatch(submitRequest());
            prev(); // Go back to the step before auth
        }, 1000);
    };

    /**
     * Apply MCP prefill data to wizard
     * Pre-fills request data and skips to auth step
     * Note: This is called AFTER getInitData completes to avoid race conditions
     */
    const applyMcpPrefill = (mcpPrefill, embed, modalId) => {
        // Set meta with MCP source
        dispatch(
            updateMeta({
                embed,
                modalId,
                modal: !embed,
                source: 'mcp',
                startedAt: new Date(),
            })
        );

        // Set request data from prefill
        dispatch(updateRequest(mcpPrefill.request));

        // Mark steps as complete based on prefilled data
        // Use a thunk to get the CURRENT steps from Redux (not stale closure)
        dispatch((dispatch, getState) => {
            const currentSteps = getState().requestWizard.options.steps;
            dispatch(
                updateOptions(
                    'steps',
                    getCompletedSteps(currentSteps, mcpPrefill.completedSteps)
                )
            );
        });

        // Wait for state to update, then navigate to auth step
        setTimeout(() => {
            if (!embed) {
                showModal(modalId);
            }

            // Navigate directly to auth step by name
            toStep('auth');
        }, 500);
    };

    /**
     * MARK: Validation
     */

    const canContinue = () => {
        if (!activeStep || activeStep?.error || status.submitting) return false;

        if (activeStep.skippable) return true;
        if (activeStep.isComplete) return true;

        return false;
    };

    const canSubmit = () => {
        if (status.submitting || status.submitted) return false;

        const inCompleteSteps =
            availableSteps.filter((step) => !step.isComplete || step.error) ||
            [];

        if (!inCompleteSteps.length) return true;

        return false;
    };

    return { init, exit, submit, showModal, hideModal, canContinue, canSubmit };
};

export default useWizard;
